<script setup>
// InteliChat #41 - first-class alert controls (surfaced in the HUD, not
// buried in native profile settings): sound on/off + volume + tone
// choice (5 native tones) + the CONTEXTUAL desktop-notification enable
// (permission asked ONLY on this button click - never on page load).
import { ref, computed } from 'vue';
import { TONES } from './sound';

const props = defineProps({
  prefs: { type: Object, required: true },
  engine: { type: Object, required: true },
});
const emit = defineEmits(['set-pref', 'toggle-sound']);

const open = ref(false);
const permission = ref(props.engine.notificationPermission());

const notifLabel = computed(() => {
  if (permission.value === 'granted') return 'Desktop alerts on';
  if (permission.value === 'denied') return 'Desktop alerts blocked';
  if (permission.value === 'unsupported') return 'Alerts unsupported';
  return 'Turn on desktop alerts';
});

const soundSuspended = computed(() => props.engine.soundSuspended());

const enableNotifications = async () => {
  const result = await props.engine.requestNotificationPermission();
  permission.value = result;
  if (result === 'granted') emit('set-pref', 'notificationsOn', true);
};

const testSound = async () => {
  await props.engine.primeSound();
  props.engine.testTone(props.prefs);
};

const onVolume = e => emit('set-pref', 'volume', Number(e.target.value));
const onTone = e => {
  emit('set-pref', 'tone', e.target.value);
};
</script>

<template>
  <div class="ic41-ctl">
    <button
      class="ic41-ctl__btn"
      type="button"
      :class="{ 'ic41-ctl__btn--off': !prefs.soundOn }"
      :title="prefs.soundOn ? 'Mute alert sound' : 'Unmute alert sound'"
      @click="emit('toggle-sound')"
    >
      <span aria-hidden="true">{{ prefs.soundOn ? '🔊' : '🔇' }}</span>
    </button>
    <button
      class="ic41-ctl__btn"
      type="button"
      title="Alert settings"
      @click="open = !open"
    >
      <span aria-hidden="true">⚙</span>
    </button>

    <div v-if="open" class="ic41-ctl__pop" @click.stop>
      <div class="ic41-ctl__row">
        <label class="ic41-ctl__lbl">Alert sound</label>
        <button
          class="ic41-ctl__pill"
          type="button"
          :class="{ 'ic41-ctl__pill--on': prefs.soundOn }"
          @click="emit('toggle-sound')"
        >{{ prefs.soundOn ? 'On' : 'Off' }}</button>
      </div>

      <div class="ic41-ctl__row">
        <label class="ic41-ctl__lbl">Volume</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          :value="prefs.volume"
          :disabled="!prefs.soundOn"
          @input="onVolume"
        />
      </div>

      <div class="ic41-ctl__row">
        <label class="ic41-ctl__lbl">Tone</label>
        <select :value="prefs.tone" :disabled="!prefs.soundOn" @change="onTone">
          <option v-for="t in TONES" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>

      <button class="ic41-ctl__test" type="button" @click="testSound">
        Test sound
      </button>
      <p v-if="prefs.soundOn && soundSuspended" class="ic41-ctl__hint">
        Click anywhere once to enable sound alerts.
      </p>

      <hr class="ic41-ctl__hr" />

      <button
        class="ic41-ctl__notif"
        type="button"
        :disabled="permission === 'granted' || permission === 'denied' || permission === 'unsupported'"
        @click="enableNotifications"
      >
        {{ notifLabel }}
      </button>
      <p v-if="permission === 'denied'" class="ic41-ctl__hint">
        Re-enable in your browser's site settings.
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ic41-ctl {
  position: relative;
  display: flex;
  gap: 4px;
  align-items: center;
}

.ic41-ctl__btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid var(--il-line, rgba(20, 24, 45, 0.09));
  background: var(--il-card, #fff);
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  display: grid;
  place-items: center;

  &--off {
    color: var(--il-bad, #c24230);
    border-color: var(--il-bad-soft, #f8ece9);
    background: var(--il-bad-soft, #f8ece9);
  }
}

.ic41-ctl__pop {
  position: absolute;
  top: 34px;
  right: 0;
  width: 232px;
  z-index: 60;
  background: var(--il-card, #fff);
  border: 1px solid var(--il-line-2, rgba(20, 24, 45, 0.15));
  border-radius: 12px;
  box-shadow: var(--il-sh-lg, 0 24px 60px -12px rgba(22, 26, 44, 0.26));
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ic41-ctl__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  input[type='range'] {
    flex: 1 1 auto;
  }
  select {
    flex: 0 0 96px;
    font-size: 12px;
    padding: 3px 6px;
    border-radius: 7px;
    border: 1px solid var(--il-line, rgba(20, 24, 45, 0.09));
    background: var(--il-card, #fff);
    color: var(--il-ink, #161a2c);
    text-transform: capitalize;
  }
}

.ic41-ctl__lbl {
  font-size: 12px;
  font-weight: 600;
  color: var(--il-ink-2, #4a5170);
}

.ic41-ctl__pill {
  border: none;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: var(--il-silver-soft, #edeff3);
  color: var(--il-ink-3a, #6b7290);

  &--on {
    background: var(--il-good-soft, #e9f2ed);
    color: var(--il-good, #2f7d5b);
  }
}

.ic41-ctl__test,
.ic41-ctl__notif {
  border: 1px solid var(--il-line, rgba(20, 24, 45, 0.09));
  border-radius: 9px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  background: var(--il-card, #fff);
  color: var(--il-ink, #161a2c);

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}

.ic41-ctl__notif {
  background: var(--il-accent-soft, #f2eafe);
  border-color: var(--il-accent-line, #e4d4f7);
  color: var(--il-accent, #8b3df0);
}

.ic41-ctl__hr {
  border: none;
  border-top: 1px solid var(--il-line, rgba(20, 24, 45, 0.09));
  margin: 2px 0;
}

.ic41-ctl__hint {
  font-size: 11px;
  color: var(--il-ink-3a, #6b7290);
  margin: 0;
}
</style>
